import React, { useMemo } from 'react'
import Badge from '../../common/badge/Badge'
import { BadgeColor } from '../../common/badge/badgeTypes'
import type {
    KitConvoTranscriptRawDataDto,
    KitConvoTranscriptTurnDto,
} from '@/api/kit/KitApi'

type Props = {
    activeConvoId: string | null
    transcriptRawData: KitConvoTranscriptRawDataDto | null
    transcriptTurns: KitConvoTranscriptTurnDto[]
}

type TimelineTurnItem = {
    id: string
    turn: KitConvoTranscriptTurnDto
    toolCalls: unknown[]
    toolResults: unknown[]
    toolCallNames: string[]
    toolResultNames: string[]
}

const readRecord = (value: unknown): Record<string, unknown> | null => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return null
    }
    return value as Record<string, unknown>
}

const formatElapsedSeconds = (value: number | null | undefined): string => {
    const totalSeconds =
        typeof value === 'number' && Number.isFinite(value)
            ? Math.max(0, Math.trunc(value))
            : 0

    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60

    const paddedSeconds = seconds.toString().padStart(2, '0')
    if (hours > 0) {
        return `${hours}:${minutes.toString().padStart(2, '0')}:${paddedSeconds}`
    }

    const totalMinutes = Math.floor(totalSeconds / 60)
    return `${totalMinutes}:${paddedSeconds}`
}

const getDayOrdinalSuffix = (day: number): string => {
    const moduloTen = day % 10
    const moduloHundred = day % 100

    if (moduloHundred >= 11 && moduloHundred <= 13) {
        return 'th'
    }
    if (moduloTen === 1) {
        return 'st'
    }
    if (moduloTen === 2) {
        return 'nd'
    }
    if (moduloTen === 3) {
        return 'rd'
    }
    return 'th'
}

const formatUkDateTime = (date: Date): string => {
    const day = date.getDate()
    const suffix = getDayOrdinalSuffix(day)
    const month = date.toLocaleString('en-GB', { month: 'short' })
    const year = date.getFullYear()
    const time = date.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    })

    return `${day}${suffix} ${month} ${year}, ${time}`
}

const formatTimestamp = (value: string | null): string => {
    if (!value) return '-'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) {
        return value
    }
    return formatUkDateTime(date)
}

const formatUnixTimestamp = (value: number | null): string => {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
        return '-'
    }

    return formatUkDateTime(new Date(value * 1000))
}

const parseToolPayloadArray = (value: string | null | undefined): unknown[] => {
    if (!value) {
        return []
    }

    const trimmed = value.trim()
    if (!trimmed) {
        return []
    }

    try {
        const parsed = JSON.parse(trimmed)
        return Array.isArray(parsed) ? parsed : []
    } catch {
        return []
    }
}

const readTrimmedString = (value: unknown): string | null => {
    if (typeof value !== 'string') {
        return null
    }
    const trimmed = value.trim()
    return trimmed ? trimmed : null
}

const extractToolName = (payload: unknown): string | null => {
    const root = readRecord(payload)
    if (!root) {
        return null
    }

    const direct = [
        root.tool_name,
        root.toolName,
        root.name,
        root.tool,
        root.tool_id,
        root.toolId,
    ]

    for (const value of direct) {
        const parsed = readTrimmedString(value)
        if (parsed) {
            return parsed
        }
    }

    const nested = [
        root.tool_call,
        root.toolCall,
        root.result,
        root.data,
        root.tool_result,
        root.toolResult,
    ]

    for (const nestedValue of nested) {
        const nestedRecord = readRecord(nestedValue)
        if (!nestedRecord) {
            continue
        }

        const parsed = extractToolName(nestedRecord)
        if (parsed) {
            return parsed
        }
    }

    return null
}

const extractToolNames = (payloads: unknown[]): string[] => {
    const names: string[] = []
    const seen = new Set<string>()

    for (const payload of payloads) {
        const name = extractToolName(payload)
        if (!name) {
            continue
        }

        const normalized = name.trim().toLowerCase()
        if (!normalized || seen.has(normalized)) {
            continue
        }

        seen.add(normalized)
        names.push(name.trim())
    }

    return names
}

const normalizeRole = (value: string): 'agent' | 'user' | 'system' => {
    const normalizedRole = value.trim().toLowerCase()
    if (normalizedRole === 'agent' || normalizedRole === 'assistant') {
        return 'agent'
    }
    if (
        normalizedRole === 'user' ||
        normalizedRole === 'human' ||
        normalizedRole === 'candidate'
    ) {
        return 'user'
    }
    return 'system'
}

const formatRoleLabel = (role: string): string => {
    const normalizedRole = normalizeRole(role)
    if (normalizedRole === 'agent') return 'Agent'
    if (normalizedRole === 'user') return 'User'
    return role.trim() || 'System'
}

const buildTimelineItems = (
    transcriptTurns: KitConvoTranscriptTurnDto[],
): TimelineTurnItem[] => {
    const orderedTurns = [...transcriptTurns].sort((a, b) => a.index - b.index)

    return orderedTurns.map((turn) => {
        const toolCalls = parseToolPayloadArray(turn.toolCallJson)
        const toolResults = parseToolPayloadArray(turn.toolResultsJson)

        return {
            id: `turn-${turn.id}`,
            turn,
            toolCalls,
            toolResults,
            toolCallNames: extractToolNames(toolCalls),
            toolResultNames: extractToolNames(toolResults),
        }
    })
}

export default function KitConversationTimeline({
    activeConvoId,
    transcriptRawData,
    transcriptTurns,
}: Props): React.JSX.Element {
    const timelineItems = useMemo(
        () => buildTimelineItems(transcriptTurns),
        [transcriptTurns],
    )

    const transcriptToolCallCount = useMemo(
        () =>
            timelineItems.reduce(
                (total, item) => total + item.toolCalls.length,
                0,
            ),
        [timelineItems],
    )

    const transcriptToolResultCount = useMemo(
        () =>
            timelineItems.reduce(
                (total, item) => total + item.toolResults.length,
                0,
            ),
        [timelineItems],
    )

    return (
        <div className="mt-6 border-t border-border pt-4">
            <div className="flex items-center justify-between gap-2">
                <h3 className="text-sm font-semibold text-foreground">
                    Conversation Timeline
                </h3>
                <span className="text-[11px] text-muted-foreground">
                    {timelineItems.length} turns • {transcriptToolCallCount}{' '}
                    tool calls • {transcriptToolResultCount} tool results
                </span>
            </div>
            <p className="mt-1 text-[11px] text-muted-foreground">
                ElevenLabs transcript turns only, with per-turn tool payloads.
            </p>

            {transcriptRawData ? (
                <div className="mt-3 rounded-xl border border-border/70 bg-secondary/20 p-3 text-[11px] text-muted-foreground">
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                        <span>
                            Event:{' '}
                            <span className="font-semibold text-foreground">
                                {transcriptRawData.eventType || '-'}
                            </span>
                        </span>
                        <span>
                            Event time:{' '}
                            <span className="font-semibold text-foreground">
                                {formatUnixTimestamp(
                                    transcriptRawData.eventTimestamp,
                                )}
                            </span>
                        </span>
                        <span>
                            Stored:{' '}
                            <span className="font-semibold text-foreground">
                                {formatTimestamp(transcriptRawData.createdAt)}
                            </span>
                        </span>
                    </div>
                </div>
            ) : null}

            {!activeConvoId ? (
                <p className="mt-3 text-xs text-muted-foreground">
                    Start a Kit conversation to see a transcript timeline.
                </p>
            ) : timelineItems.length === 0 ? (
                <p className="mt-3 text-xs text-muted-foreground">
                    No transcript turns captured yet.
                </p>
            ) : (
                <div className="mx-auto mt-3 max-w-[620px] rounded-2xl border border-sky-200/60 bg-gradient-to-b from-sky-50/70 to-white p-3">
                    <div className="space-y-3">
                        {timelineItems.map((item) => {
                            const normalizedRole = normalizeRole(item.turn.role)
                            const isAgent = normalizedRole === 'agent'
                            const isUser = normalizedRole === 'user'
                            const message = item.turn.message?.trim() ?? ''
                            const hasToolCalls = item.toolCalls.length > 0
                            const hasToolResults = item.toolResults.length > 0

                            return (
                                <div
                                    key={item.id}
                                    className={`flex ${
                                        isAgent
                                            ? 'justify-start pr-10'
                                            : isUser
                                              ? 'justify-end pl-10'
                                              : 'justify-center px-6'
                                    }`}
                                >
                                    <div
                                        className={`max-w-[85%] rounded-2xl border px-3 py-2 shadow-sm ${
                                            isAgent
                                                ? 'rounded-bl-md border-sky-200 bg-sky-100/95'
                                                : isUser
                                                  ? 'rounded-br-md border-gray-200 bg-white'
                                                  : 'border-gray-300 bg-gray-100'
                                        }`}
                                    >
                                        <div className="flex items-center justify-between gap-3 text-[10px] uppercase tracking-wide text-muted-foreground">
                                            <span>
                                                {formatRoleLabel(
                                                    item.turn.role,
                                                )}
                                            </span>
                                            <span>
                                                {formatElapsedSeconds(
                                                    item.turn
                                                        .elapsedSecondsSinceCallStart,
                                                )}
                                            </span>
                                        </div>

                                        {message ? (
                                            <p className="mt-1 whitespace-pre-wrap text-xs leading-relaxed text-foreground">
                                                {message}
                                            </p>
                                        ) : hasToolCalls && hasToolResults ? (
                                            <p className="mt-1 text-xs italic text-muted-foreground">
                                                [Tool call and result]
                                            </p>
                                        ) : hasToolCalls ? (
                                            <p className="mt-1 text-xs italic text-muted-foreground">
                                                [Tool call initiated]
                                            </p>
                                        ) : hasToolResults ? (
                                            <p className="mt-1 text-xs italic text-muted-foreground">
                                                [Tool result returned]
                                            </p>
                                        ) : (
                                            <p className="mt-1 text-xs italic text-muted-foreground">
                                                [No spoken text captured]
                                            </p>
                                        )}

                                        <div className="mt-2 flex flex-wrap items-center gap-2">
                                            <Badge color={BadgeColor.Neutral}>
                                                Turn {item.turn.index}
                                            </Badge>
                                            {item.turn.isError ? (
                                                <Badge color={BadgeColor.Error}>
                                                    Tool result error
                                                </Badge>
                                            ) : null}
                                            {hasToolCalls ? (
                                                <Badge
                                                    color={BadgeColor.Warning}
                                                >
                                                    {item.toolCalls.length} tool
                                                    calls
                                                </Badge>
                                            ) : null}
                                            {hasToolResults ? (
                                                <Badge
                                                    color={BadgeColor.Warning}
                                                >
                                                    {item.toolResults.length}{' '}
                                                    tool results
                                                </Badge>
                                            ) : null}
                                        </div>

                                        {item.toolCallNames.length > 0 ? (
                                            <p className="mt-2 text-[11px] text-muted-foreground">
                                                Tool calls:{' '}
                                                <span className="font-semibold text-foreground">
                                                    {item.toolCallNames.join(
                                                        ', ',
                                                    )}
                                                </span>
                                            </p>
                                        ) : null}

                                        {item.toolResultNames.length > 0 ? (
                                            <p className="mt-1 text-[11px] text-muted-foreground">
                                                Tool results:{' '}
                                                <span className="font-semibold text-foreground">
                                                    {item.toolResultNames.join(
                                                        ', ',
                                                    )}
                                                </span>
                                            </p>
                                        ) : null}

                                        {hasToolCalls ? (
                                            <details className="mt-2 text-[11px] text-muted-foreground">
                                                <summary className="cursor-pointer text-primary">
                                                    Tool calls payload
                                                </summary>
                                                <pre className="mt-1 max-h-40 overflow-auto rounded-lg bg-white/80 p-2 text-[10px] leading-relaxed text-foreground">
                                                    {JSON.stringify(
                                                        item.toolCalls,
                                                        null,
                                                        2,
                                                    )}
                                                </pre>
                                            </details>
                                        ) : null}

                                        {hasToolResults ? (
                                            <details className="mt-2 text-[11px] text-muted-foreground">
                                                <summary className="cursor-pointer text-primary">
                                                    Tool results payload
                                                </summary>
                                                <pre className="mt-1 max-h-40 overflow-auto rounded-lg bg-white/80 p-2 text-[10px] leading-relaxed text-foreground">
                                                    {JSON.stringify(
                                                        item.toolResults,
                                                        null,
                                                        2,
                                                    )}
                                                </pre>
                                            </details>
                                        ) : null}
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>
            )}
        </div>
    )
}
